import React, { useMemo, useRef, useState, useCallback, useEffect } from "react";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { compactSelect, compactSelectError } from "../../../design/selectStyle";
import { useOpenBanking } from "../../OpenBanking/UseOpenBanking";
import AsyncSelect from "react-select/async";
import { Dialog } from "primereact/dialog";
import EditSellerBankAccountModal from "./EditSellerBankAccountModal";
import { mapContactToBuyerBankAccount, mapContactToSellerBankAccount, validateSellerBankAccount, defaultPaymentOrder, paymentOrderValid } from "./PaymentOrderUtils";
import { useAppContext } from "../../../AppContext";
import debounce from "lodash/debounce";
import { loadOptionsForBankAccounts } from "../../OpenBanking/OpenBankingUtils";
import { swalAlert } from "../../../services/functions";
import { checkForAccountErrors } from "./PaymentOrderService";

const PaymentBankAccounts = ({
  paymentOrder,
  setPaymentOrder,
  validationErrors,
  setValidationErrors,
  refreshData,
  handleChange,
  disabled,
  editBankAccountModal: externalEditBankAccountModal,
  setEditBankAccountModal: externalSetEditBankAccountModal,
}) => {
  const { t } = useTranslation("translations");
  const {user:[user],company:[company]}= useAppContext()
  const { bankAccounts } = useOpenBanking();
  const inputRef = useRef(null);
  const defaultsSetRef = useRef(false);

  const [localEditBankAccountModal, setLocalEditBankAccountModal] = useState({ show: false });
  const editBankAccountModal = externalEditBankAccountModal ?? localEditBankAccountModal;
  const setEditBankAccountModal = externalSetEditBankAccountModal ?? setLocalEditBankAccountModal;

  const [sellerBankAccountOptions, setSellerBankAccountOptions] = useState([]);
  const [isSellerDropdownOpen, setIsSellerDropdownOpen] = useState(false);
  const sellerSearchRef = useRef(false);
  const selectedSellerOptionRef = useRef(null);

  const selectedSellerOption = sellerBankAccountOptions.find(
    option => option?.contact?.name === paymentOrder?.sellerBankAccountTransactional?.sellerName
  ) || (paymentOrder?.sellerBankAccountTransactional?.sellerName ? {
    label: paymentOrder?.sellerBankAccountTransactional?.sellerName,
    contact: { name: paymentOrder?.sellerBankAccountTransactional?.sellerName }
  } : null);


    const filteredBankAccounts = useMemo(() => {
      return (bankAccounts || []).filter(account => account.isOpenBanking);
    }, [bankAccounts]);

  const extractSellerBankAccountErrors =useCallback((errors) => {
    if (!errors) return {};
    const extracted = {};
    Object.keys(errors).forEach(key => {
      if (key.startsWith('sellerBankAccountTransactional.')) {
        const fieldName = key.replace('sellerBankAccountTransactional.', '');
        extracted[fieldName] = errors[key];
      }
    });
    return extracted;
  },[]);

  const debouncedLoadOptions = useMemo(
    () =>
      debounce(async (inputValue,sellerNipt, setValue,callback) => {
        const data = await loadOptionsForBankAccounts(inputValue, "SUPPLIER", true,sellerNipt);
        callback(data);
        if(setValue ){
          if( data && data.length >=1 ){
            const defaultAccount = data.find(acc => acc.financialInstitution?.name === "Banka Kombëtare Tregtare");
            handleSellerAccountChange( defaultAccount?? data[0],null,true)
          }
      }
      }, 500),
    []
  );
  const loadOptions = useCallback((inputValue,sellerNipt,setValue) => {
    return new Promise((resolve) => {
      debouncedLoadOptions(inputValue,sellerNipt,setValue, resolve);
    });
  }, [debouncedLoadOptions]);

  useEffect(() => {
    if (defaultsSetRef.current) return;
    const isCreateFlow = !paymentOrder?.id;
    const noAccountSelected = !paymentOrder?.buyerBankAccountTransactional;
    if (!isCreateFlow || !noAccountSelected) return;
    if (!filteredBankAccounts?.length || !company) return;

    const defaultAccount = filteredBankAccounts.find(
      acc => acc.financialInstitution?.name === "Banka Kombëtare Tregtare"
    );

    if (defaultAccount) {
      defaultsSetRef.current = true;
      const buyerBankAccountTransactional = mapContactToBuyerBankAccount({account: defaultAccount, company});
      handleChange(null, "buyerBankAccountTransactional", buyerBankAccountTransactional);
    }
  }, [filteredBankAccounts, paymentOrder?.id, paymentOrder?.buyerBankAccountTransactional, handleChange, company]);
  
  const handleSellerAccountChange = useCallback(async (contactAccount, reason,validate) => {
    if (reason?.action === "input-blur" || reason?.action === "menu-close") return;
    if (reason?.action === "set-value") return;

    let sellerBankAccountTransactional = null;
    if(contactAccount){
      sellerBankAccountTransactional = mapContactToSellerBankAccount(contactAccount);
      selectedSellerOptionRef.current = contactAccount;

      const currentSellerNipt = paymentOrder?.sellerBankAccountTransactional?.sellerNipt;
      const newSellerNipt = sellerBankAccountTransactional?.sellerNipt;
      const hasExistingInvoices = paymentOrder?.paymentOrderRecords?.length > 0;

      if (hasExistingInvoices && (currentSellerNipt && currentSellerNipt !== newSellerNipt)) {
        const result = await swalAlert({
          title: t("paymentOrder.sellerChangeConfirm"),
          t
        });

        if (!result) {
          setIsSellerDropdownOpen(false);
          return;
        }
        setPaymentOrder(prev => ({
          ...prev,
          paymentOrderRecords: defaultPaymentOrder.paymentOrderRecords,
          sellerBankAccountTransactional
        }));
        setIsSellerDropdownOpen(false);
        return;
      }
    }

    handleChange(null,"sellerBankAccountTransactional",sellerBankAccountTransactional);
    setIsSellerDropdownOpen(false);
  }, [paymentOrder, t, handleChange, setEditBankAccountModal]);

  useEffect(() => {
    if (sellerSearchRef.current) return;
    const isCreateFlow = !paymentOrder?.id;
    const sellerName = paymentOrder?.sellerBankAccountTransactional?.sellerName;
    const sellerNipt = paymentOrder?.sellerBankAccountTransactional?.sellerNipt;
    if (!sellerName || !isCreateFlow) return;

    sellerSearchRef.current = true;
    loadOptions(null,sellerNipt,true).then((options) => {
      setSellerBankAccountOptions(options || []);
    });
  }, [paymentOrder?.sellerBankAccountTransactional?.sellerName, paymentOrder?.id, loadOptions]);

  const checkForSellerBankAccountErrors=useCallback((errors)=>checkForAccountErrors(errors,"seller"),[validationErrors])
  const checkforBuyerBankAccountErrors=useCallback((errors)=>checkForAccountErrors(errors,"buyer"),[validationErrors])

console.log({validationErrors})
  return (
    <>
      <div className="row mx-1 mx-md-auto">
        <div className="col-12 col-md-6 pt-0 border-right">
          <div className="row">
            <div className="d-flex justify-content-between header border-bottom w-full pl-3">
              <div>
                <label>{t("openBanking.buyerBankAccount")}</label>
              </div>
            </div>
            <div className="col-12 pt-0 pt-2">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <label className="field-label" htmlFor="buyerBankAccounts">
                    {t("openBanking.bankAccounts")}
                  </label>
                </div>
              </div>
              <Select
                options={filteredBankAccounts}
                getOptionLabel={(option) =>
                  option?.iban + "-" + option?.currency + "-" + option.financialInstitution?.name
                }
                styles={checkforBuyerBankAccountErrors(validationErrors) ? compactSelectError : compactSelect}
                getOptionValue={(option) => option.iban}
                placeholder={paymentOrder?.buyerIban}
                value={filteredBankAccounts.find(bAcc=>bAcc.iban===paymentOrder?.buyerBankAccountTransactional?.buyerIban) }
                onChange={(account) => {
                  let buyerBankAccountTransactional 
                  if(account){
                     buyerBankAccountTransactional = mapContactToBuyerBankAccount({account,user,company})
                  }
                  handleChange(null, "buyerBankAccountTransactional", buyerBankAccountTransactional);
                }}
                isDisabled={disabled}
                isClearable={true}
              />
              <div>
                <div className="field-label">IBAN</div>
                <div className="seller-invoice">{paymentOrder?.buyerBankAccountTransactional?.buyerIban}</div>
              </div>
              <div>
                <div className="field-label">{t("openBanking.bankName")}</div>
                <div className="seller-invoice">
                  {paymentOrder?.buyerBankAccountTransactional?.buyerBankName}
                </div>
              </div>
              <div>
                <div className="field-label">{t("openBanking.bankAccountNumber")}</div>
                <div className="seller-invoice">{paymentOrder?.buyerBankAccountTransactional?.buyerAccountReference}</div>
              </div>
              <div>
                <div className="field-label">{t("openBanking.currency")}</div>
                <div className="seller-invoice">{paymentOrder?.buyerBankAccountTransactional?.buyerAccountReferenceCurrency}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-6 pt-0">
          <div className="row">
            {!disabled && <div className="d-flex justify-content-between header border-bottom w-full pl-3">
              <div className="d-flex justify-content-between">
                <label>{t("openBanking.sellerBankAccount")}</label>
                <i
                  onClick={() => setEditBankAccountModal({ show: true })}
                  className="custom-target-icon pi pi-user-edit p-text-secondary p-overlay-badge text-success"
                  data-tooltip-id="global-tooltip" data-tooltip-content="Edit"
                  style={{ fontSize: "1.5rem", cursor: "pointer", marginLeft: "12px" }}
                ></i>
              </div>
            </div>}
            <div className="col-12 pt-0 pt-2">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <label className="field-label" htmlFor="buyerBankAccounts">
                    {t("openBanking.vendorName")}
                  </label>
                </div>
              </div>
              <AsyncSelect
                styles={ checkForSellerBankAccountErrors(validationErrors)  ? compactSelectError : compactSelect}
                name="name"
                ref={inputRef}
                menuIsOpen={isSellerDropdownOpen}
                onMenuOpen={() => setIsSellerDropdownOpen(true)}
                onMenuClose={() => {
                  setIsSellerDropdownOpen(false);
                }}
                components={{
                  DropdownIndicator: () => null,
                  IndicatorSeparator: () => null,
                }}
                className={`basic-multi-select valid`}
                onChange={handleSellerAccountChange}
                cacheOptions
                value={selectedSellerOption}
                loadOptions={loadOptions}
                defaultOptions={sellerBankAccountOptions.length > 0 ? sellerBankAccountOptions : false}
                onInputChange={(input,reason) => {
                      if (reason?.action === "input-blur" || reason?.action === "menu-close")
                    return;
                  if (reason?.action === "set-value") return;
                  handleChange(null,"sellerBankAccountTransactional.sellerName",input)
                }}
                isClearable={true}
                isDisabled={disabled}
              />

              <div>
                <div className="field-label">IBAN</div>
                <div className="seller-invoice">{paymentOrder?.sellerBankAccountTransactional?.sellerIban}</div>
              </div>
              <div>
                <div className="field-label">{t("openBanking.bankName")}</div>
                <div className="seller-invoice">
                  {paymentOrder?.sellerBankAccountTransactional?.creditorBankName}
                </div>
              </div>
              <div>
                <div className="field-label">{t("openBanking.bankAccount")}</div>
                <div className="seller-invoice">{paymentOrder?.sellerBankAccountTransactional?.creditorAccountReference}</div>
              </div>
              <div>
                <div className="field-label">{t("openBanking.currency")}</div>
                <div className="seller-invoice">{paymentOrder?.sellerBankAccountTransactional?.creditorAccountReferenceCurrency}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        header={t("openBanking.editSellerBankAccount")}
        visible={editBankAccountModal.show}
        onHide={() => setEditBankAccountModal({ show: false })}
        style={{ width: "30vw",zIndex:10000 }}
        breakpoints={{ "960px": "75vw", "641px": "100vw" }}
        closable={true}
      >
        {editBankAccountModal?.show && <EditSellerBankAccountModal
          paymentOrder={paymentOrder}
          sellerBankAccountTransactional={paymentOrder?.sellerBankAccountTransactional}
          errorsAbove={extractSellerBankAccountErrors(validationErrors)}
          onSave={(updatedBankAccount) => {
            setValidationErrors({});
            handleChange(null, "sellerBankAccountTransactional", updatedBankAccount);
            paymentOrderValid({data:{...paymentOrder,sellerBankAccountTransactional:updatedBankAccount},setValidationErrors,t})
            setEditBankAccountModal({ show: false });

          }}
        />}
      </Dialog>
    </>
  );
};

export default PaymentBankAccounts;
